import { AxiosContext } from '@/context/Axios'
import { useAuth } from '@/hooks/common'
import { Note, ReactQueryKeys, SearchType, Topic } from '@/types'
import {
  DEFAULT_TOPIC_APPLICATION,
  PrivacyType,
  TopicType,
  TopicTypeAndApplicationDic,
  whiteListFilterFn,
} from '@/utils/constants'
import { useMutation } from '@tanstack/react-query'
import { useContext, useMemo, useState } from 'react'
import { GetTopicParam } from './useGetTopics'
import { hasRestParameter } from 'typescript'
import { canLoadMore } from '@/utils/helper'

export const useMangePinCollections = (
  topicType: TopicType = TopicType.PIN_COLLECTION,
  privateType: PrivacyType = PrivacyType.PRIVATE,
) => {
  const axios = useContext(AxiosContext)
  const { user: currentUser } = useAuth()

  const [pinCollections, setPinCollections] = useState<Topic[]>([])
  const [pinsPagination, setPinsPagination] = useState<any>()
  const [loadingNextPage, setLoadingNextPage] = useState(false)

  const reset = () => {
    setLoadingNextPage(false)
    setPinCollections([])
    setPinsPagination(null)
  }

  const hasMore = useMemo(() => {
    return pinsPagination ? canLoadMore(pinsPagination) : false
  }, [pinsPagination])

  const fetchPins = (pageNo: number = 1) => {
    fetchTopics.mutateAsync({ pageNo, interaction: privateType, topicType })
  }

  const fetchTopics = useMutation({
    mutationKey: [ReactQueryKeys.GET_TOPICS, new Date().getTime()],
    mutationFn: async (data: GetTopicParam) => {
      if (data.pageNo === 1) {
        reset()
      } else {
        setLoadingNextPage(true)
      }
      const response = await axios.api.post('ajax/topics', {
        requestType: 'topicList',
        list: 'mini',
        format: 'json',
        searchType: SearchType.loggedInUserContent,
        topicApplication: TopicTypeAndApplicationDic[topicType] || DEFAULT_TOPIC_APPLICATION,
        ...data,
      })
      setLoadingNextPage(false)

      const topics = response.data.myTopics || []

      const filtered = topics.filter(whiteListFilterFn)
      if (data.pageNo === 1) setPinCollections(filtered)
      else {
        setPinCollections((prev) => [...prev, ...filtered])
      }
      setPinsPagination(response.data.pagination)
      return response.data?.myTopics || []
    },
  })

  const loadMore = () => {
    if (!fetchTopics.isLoading && pinsPagination) {
      const nextPage = pinsPagination.currentPage + 1
      fetchPins(nextPage)
    }
  }

  return {
    fetchPins,
    pinCollections,
    pagination: pinsPagination,
    loadMore,
    hasMore,
    loadingNextPage,
    isLoading: fetchTopics.isLoading,
  }
}
